import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Store, Package, ShoppingBag, DollarSign, TrendingUp, Plus, Settings, 
  LogOut, ExternalLink, Eye, Edit, Trash2, Copy, Key, CreditCard, AlertCircle,
  BarChart3, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import MerchantAnalytics from '@/components/MerchantAnalytics';
import MerchantPayouts from '@/components/MerchantPayouts';
import WhatsAppNumberEditor from '@/components/WhatsAppNumberEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MerchantDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('merchant_token');
    if (!token) {
      navigate('/merchant/login');
      return;
    }
    loadDashboard();
  }, [navigate]);

  const loadDashboard = async () => {
    const token = localStorage.getItem('merchant_token');
    try {
      // Load merchant profile
      const profileRes = await fetch(`${API_URL}/api/merchant/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profileData = await profileRes.json();
      if (profileData.success) {
        setMerchant(profileData.merchant);
      }

      // Load dashboard stats
      const dashRes = await fetch(`${API_URL}/api/merchant/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dashData = await dashRes.json();
      if (dashData.success) {
        setDashboard(dashData.dashboard);
      }

      // Load products
      const prodRes = await fetch(`${API_URL}/api/merchant/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const prodData = await prodRes.json();
      if (prodData.success) {
        setProducts(prodData.products);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('merchant_token');
    localStorage.removeItem('merchant_data');
    navigate('/merchant/login');
  };

  const copyStoreUrl = () => {
    const url = `${window.location.origin}/store/${merchant?.store_slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Store URL copied!');
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    const token = localStorage.getItem('merchant_token');
    try {
      const res = await fetch(`${API_URL}/api/merchant/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Product deleted');
        setProducts(products.filter(p => p.id !== productId));
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">SS</span>
              </div>
              <span className="text-white font-bold">SALESSPACE</span>
            </Link>
            <span className="text-slate-500">|</span>
            <span className="text-slate-300">Merchant Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={copyStoreUrl}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-300"
            >
              <ExternalLink className="w-4 h-4" />
              View Store
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1.5 text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Banner */}
        {merchant?.status === 'pending' && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <div>
              <p className="text-yellow-400 font-medium">Account Pending Approval</p>
              <p className="text-sm text-yellow-400/70">Your merchant account is under review. You&apos;ll be notified once approved.</p>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Welcome, {merchant?.business_name}</h1>
          <p className="text-slate-400 mt-1">
            Store URL: <button onClick={copyStoreUrl} className="text-blue-400 hover:underline inline-flex items-center gap-1">
              /store/{merchant?.store_slug} <Copy className="w-3 h-3" />
            </button>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Products</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.total_products || 0}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Package className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Sales</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.total_sales || 0}</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-lg">
                <ShoppingBag className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-white mt-1">${(stats.total_revenue || 0).toFixed(2)}</p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Commission Earned</p>
                <p className="text-2xl font-bold text-white mt-1">${(stats.total_commission_earned || 0).toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1">{stats.commission_rate || 10}% rate</p>
              </div>
              <div className="p-3 bg-orange-500/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 overflow-x-auto">
          {['overview', 'products', 'analytics', 'payouts', 'api-config', 'settings'].map((tab) => (
            <button
              key={tab}
              data-testid={`merchant-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'analytics' && <BarChart3 className="w-4 h-4" />}
              {tab === 'payouts' && <Wallet className="w-4 h-4" />}
              {tab === 'api-config' ? 'API Config' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Products */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">Your Products</h3>
                <Link to="/merchant/products/new" className="text-blue-400 text-sm hover:underline">
                  Add New
                </Link>
              </div>
              {products.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No products yet</p>
                  <Link
                    to="/merchant/products/new"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                  >
                    <Plus className="w-4 h-4" /> Add Your First Product
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {products.slice(0, 5).map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{product.name}</p>
                        <p className="text-sm text-slate-400">${product.price}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        product.active ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'
                      }`}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/merchant/products/new"
                  className="p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl text-center transition-colors"
                >
                  <Plus className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <p className="text-white font-medium">Add Product</p>
                </Link>
                <button
                  onClick={() => setActiveTab('api-config')}
                  className="p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl text-center transition-colors"
                >
                  <Key className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <p className="text-white font-medium">API Config</p>
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className="p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl text-center transition-colors"
                >
                  <CreditCard className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-white font-medium">Bank Details</p>
                </button>
                <Link
                  to={`/store/${merchant?.store_slug}`}
                  target="_blank"
                  className="p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl text-center transition-colors"
                >
                  <Store className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                  <p className="text-white font-medium">View Store</p>
                </Link>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-white">All Products ({products.length})</h3>
              <Link
                to="/merchant/products/new"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" /> Add Product
              </Link>
            </div>
            {products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No products yet</p>
                <p className="text-slate-500 text-sm mt-1">Start by adding your first product</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-slate-700">
                      <th className="pb-3 text-slate-400 font-medium">Product</th>
                      <th className="pb-3 text-slate-400 font-medium">Price</th>
                      <th className="pb-3 text-slate-400 font-medium">Type</th>
                      <th className="pb-3 text-slate-400 font-medium">Status</th>
                      <th className="pb-3 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="border-b border-slate-700/50">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <div>
                              <p className="text-white font-medium">{product.name}</p>
                              <p className="text-xs text-slate-500">{product.category}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-white">${product.price}</td>
                        <td className="py-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            product.product_type === 'affiliate'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {product.product_type}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            product.active ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'
                          }`}>
                            {product.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/products/${product.id}`}
                              target="_blank"
                              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                            <Link
                              to={`/merchant/products/${product.id}/edit`}
                              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => deleteProduct(product.id)}
                              className="p-2 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'api-config' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-2">API Configurations</h3>
            <p className="text-slate-400 text-sm mb-6">
              Connect your supplier APIs for automated order routing and inventory sync.
            </p>
            <Link
              to="/merchant/api-config"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
            >
              <Key className="w-4 h-4" /> Manage API Keys
            </Link>
          </div>
        )}

        {activeTab === 'analytics' && (
          <MerchantAnalytics />
        )}

        {activeTab === 'payouts' && (
          <MerchantPayouts />
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4">Bank Details for Payouts</h3>
              <p className="text-slate-400 text-sm mb-4">
                Add your bank details to receive commission payouts.
              </p>
              <Link
                to="/merchant/bank-details"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm"
              >
                <CreditCard className="w-4 h-4" /> {merchant?.bank_details ? 'Update' : 'Add'} Bank Details
              </Link>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4">Store Settings</h3>
              <label className="block text-sm text-slate-300 mb-2">WhatsApp number for customer chats</label>
              <WhatsAppNumberEditor
                endpoint="/merchant/profile"
                initialValue={merchant?.whatsapp || ''}
                headers={{ Authorization: `Bearer ${localStorage.getItem('merchant_token')}` }}
                onSaved={(whatsapp) => setMerchant((m) => ({ ...m, whatsapp }))}
                dark
                testIdPrefix="merchant-whatsapp"
              />
              <p className="text-xs text-slate-500 mt-2 mb-4">Shown as "Chat with seller on WhatsApp" on your products. Leave blank to use your phone number.</p>
              <Link
                to="/merchant/settings"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm"
              >
                <Settings className="w-4 h-4" /> Edit Profile & Store
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
